import { z } from "zod";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
export const ISSUE_STATUS = z.enum([
    "open",
    "assigned",
    "in_progress",
    "resolved",
    "verified",
    "rejected",
    "ignored",
    "cancelled",
]);
/** Unresolved work, including a rejected resolution that needs another attempt. */
export const ACTIONABLE_ISSUE_STATUSES = ["open", "assigned", "in_progress", "rejected"];
export const ISSUE_TYPE = z.enum([
    "oos",
    "hidden_oos",
    "price_mismatch",
    "missing_price_tag",
    "extra_price_tag",
    "promo_missing",
    "promo_wrong",
    "posm_missing",
    "planogram_mismatch",
    "missing_facing",
    "wrong_placement",
    "extra_product",
    "low_share_of_shelf",
    "quality_issue",
    "unknown",
]);
export const SEVERITY_LEVEL = z.enum(["low", "medium", "high", "critical"]);
export const issueInfo = z.object({
    id: z.string().uuid(),
    storeId: z.string().uuid(),
    storeName: z.string().nullable(),
    reporterUserId: z.string().uuid().nullable(),
    reporterEmail: z.string().nullable(),
    assignedToUserId: z.string().uuid().nullable(),
    assigneeEmail: z.string().nullable(),
    issueType: ISSUE_TYPE,
    severity: SEVERITY_LEVEL,
    status: ISSUE_STATUS,
    title: z.string(),
    description: z.string().nullable(),
    dueAt: z.string().datetime().nullable(),
    resolvedAt: z.string().datetime().nullable(),
    verifiedAt: z.string().datetime().nullable(),
    // Proof-of-resolution: who closed/verified the issue (accountability) and the
    // evidence attached at resolve time (the "after" photo + note). All derived
    // from existing columns + metadata.resolution; null until set.
    resolvedByUserId: z.string().uuid().nullable(),
    resolvedByEmail: z.string().nullable(),
    verifiedByUserId: z.string().uuid().nullable(),
    verifiedByEmail: z.string().nullable(),
    resolutionNote: z.string().nullable(),
    /** Presigned URL of the resolution evidence ("after" photo). */
    proofImageUrl: z.string().nullable(),
    proofImageHash: z.string().nullable(),
    captureId: z.string().uuid().nullable(),
    sessionId: z.string().uuid().nullable(),
    visionRunId: z.string().uuid().nullable(),
    detectionId: z.string().uuid().nullable(),
    cropImageHash: z.string().nullable(),
    cropImageUrl: z.string().nullable(),
    // Human-readable location of the issue, resolved from the linked capture +
    // detection (for store staff — shown instead of raw ids). Null when unknown.
    fixtureCode: z.string().nullable(),
    fixtureName: z.string().nullable(),
    categoryName: z.string().nullable(),
    zoneName: z.string().nullable(),
    /** Shelf level the flagged detection sits on (render as "Shelf N"). */
    shelfLevel: z.number().int().nullable(),
    /** Configured shelf label, or an explicitly qualified image-row label. */
    shelfName: z.string().nullish(),
    shelfSource: z.enum(["physical", "image"]).nullish(),
    /** When the linked capture photo was taken. */
    capturedAt: z.string().datetime().nullable(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const createIssueRequest = z.object({
    storeId: z.string().uuid(),
    assignedToUserId: z.string().uuid().nullish(),
    issueType: ISSUE_TYPE.default("unknown"),
    severity: SEVERITY_LEVEL.default("medium"),
    title: z.string().min(1).max(500),
    description: z.string().max(5000).nullish(),
    dueAt: z.string().datetime().nullish(),
    metadata: z.record(z.unknown()).default({}),
    imageObjectKey: z.string().max(1024).optional(),
    imageSha256: z.string().max(128).optional(),
});
export const uploadIssueImageResponse = z.object({
    sha256: z.string(),
    objectKey: z.string(),
});
export const updateIssueRequest = z
    .object({
    status: ISSUE_STATUS.optional(),
    severity: SEVERITY_LEVEL.optional(),
    issueType: ISSUE_TYPE.optional(),
    assignedToUserId: z.string().uuid().nullish(),
    title: z.string().min(1).max(500).optional(),
    description: z.string().max(5000).nullish(),
    dueAt: z.string().datetime().nullish(),
    metadata: z.record(z.unknown()).optional(),
    // ── Resolution evidence (optional; typically sent with status:"resolved") ──
    /** Free-text proof note. */
    resolutionNote: z.string().max(5000).nullish(),
    /**
     * Proof "after" image as an EXTERNAL URL — the server fetches it
     * (SSRF-guarded, size/timeout-capped), validates it's a real image, and
     * re-hosts it content-addressed. Preferred path for 3rd-party systems.
     */
    resolutionProofImageUrl: z.string().url().max(2048).optional(),
    /**
     * Alternative to the URL: an objectKey already uploaded via
     * POST /api/v2/issues/upload-image (kind=proof). Used by the first-party UI.
     */
    resolutionProofObjectKey: z.string().max(1024).optional(),
    resolutionProofSha256: z.string().max(128).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: "At least one field required" });
export const listIssuesQuery = paginationQuerySchema.extend({
    statusGroup: z.enum(["actionable"]).optional(),
    // "newest" = latest created first (default in the UI); "urgency" = severity
    // then newest; "updated" = most recently touched (board default).
    sort: z.enum(["newest", "urgency", "updated"]).optional(),
    storeId: z.string().uuid().optional(),
    status: ISSUE_STATUS.optional(),
    severity: SEVERITY_LEVEL.optional(),
    issueType: ISSUE_TYPE.optional(),
    assignedToUserId: z.string().uuid().optional(),
    captureId: z.string().uuid().optional(),
    sessionId: z.string().uuid().optional(),
    /** Case-insensitive substring match on issue title or description. */
    search: z.string().max(255).optional(),
});
export const issuesListResponse = pagedResponse(issueInfo);
//# sourceMappingURL=issues.schemas.js.map